import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BusinessRegistrationForm } from '@/types/businessRegistration';
import { DEFAULT_BUSINESS_SETTINGS } from '@/types/businessSettings';
import { PersonalStep } from './PersonalStep';
import { BusinessStep } from './BusinessStep';
import { ContactStep } from './ContactStep';
import { ExperienceStep } from './ExperienceStep';
import { BusinessLocationStep } from './BusinessLocationStep';
import { ReviewStep } from './ReviewStep';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { registerBusiness, registerBusinessUser, checkEmailExists } from '@/utils/homepageApi';
import { generateUniqueSlug } from '@/utils/slugUtils';
import { useGeocoding } from '@/hooks/useGeocoding';
import { validatePersonalStep, validateBusinessStep, validateContactStep } from '@/utils/registrationValidation';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AutoSaveIndicator } from '@/components/ui/auto-save-indicator';
import { LocationConfirmationStep } from './LocationConfirmationStep';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface BusinessRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type RegistrationStep = 'personal' | 'business' | 'contact' | 'location' | 'locationConfirmation' | 'experience' | 'review';
const STEP_SEQUENCE: RegistrationStep[] = ['personal', 'business', 'contact', 'location', 'locationConfirmation', 'experience', 'review'];

const initialFormData: BusinessRegistrationForm = {
  userData: {
    email: '',
    password: '',
    name: '',
    dateOfBirth: new Date(),
    nationality: '',
    role: 'business',
  },
  businessData: {
    name: '',
    emoji: '🦁',
    description: '',
    type: 'tour_operator',
    hasAnimalActivities: false,
    hasAdventureActivities: false,
    contact: {
      email: '',
      phone: '',
      countryCode: '+1',
      website: '',
      bookingMode: 'platform',
      bookingUrl: '',
      whatsappNumber: ''
    },
    location: {
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
      },
      coordinates: {
        type: 'Point',
        coordinates: [0, 0]
      }
    },
    settings: DEFAULT_BUSINESS_SETTINGS,
    experiences: [],
    animals: [],
    additionalInfo: ''
  }
};

export function BusinessRegistrationModal({ isOpen, onClose }: BusinessRegistrationModalProps) {
  const { t } = useTranslation('business', { keyPrefix: 'registration' });
  
  // Enhanced step labels for better UX
  const STEP_LABELS = {
    personal: t('personalInfo'),
    business: t('businessDetails'), 
    contact: t('contactInfo'),
    location: t('location'),
    locationConfirmation: t('confirmLocation'),
    experience: t('experiences'),
    review: t('reviewSubmit')
  };

  const STEP_DESCRIPTIONS = {
    personal: t('yourAccountDetails'),
    business: t('businessInformation'),
    contact: t('contactDetails'),
    location: t('businessLocation'),
    locationConfirmation: t('verifyLocation'),
    experience: t('activityTypes'),
    review: t('finalReview')
  };

  const [step, setStep] = useState<RegistrationStep>('personal');
  const [formData, setFormData] = useState<BusinessRegistrationForm>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepIsLoading, setStepIsLoading] = useState(false);
  const [personalStepErrors, setPersonalStepErrors] = useState<{ [key: string]: string }>({});
  const [businessStepErrors, setBusinessStepErrors] = useState<{ [key: string]: string }>({});
  const [contactStepErrors, setContactStepErrors] = useState<{ [key: string]: string }>({});
  const [locationStepErrors, setLocationStepErrors] = useState<{ [key: string]: string }>({});
  const [experienceStepErrors, setExperienceStepErrors] = useState<{ [key: string]: string }>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingState, setSavingState] = useState<'saved' | 'saving' | 'error'>('saved');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  
  // Handle back navigation
  const handleBack = (fromStep: RegistrationStep) => {
    const currentIndex = STEP_SEQUENCE.indexOf(fromStep);
    if (currentIndex > 0) {
      goToStep(STEP_SEQUENCE[currentIndex - 1]);
    }
  };

  const { getCoordinatesFromAddress: _getCoordinatesFromAddress } = useGeocoding();

  // Enhanced step persistence with step name changes
  useEffect(() => {
    const savedData = localStorage.getItem('businessRegistration');
    const savedStep = localStorage.getItem('businessRegistrationStep');
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed);
      } catch (error) {
        console.error('Failed to parse saved registration data', error);
        localStorage.removeItem('businessRegistration');
      }
    }
    
    if (savedStep && STEP_SEQUENCE.includes(savedStep as RegistrationStep)) {
      setStep(savedStep as RegistrationStep);
    }
  }, []);

  // Auto-save functionality
  useEffect(() => {
    const saveData = () => {
      if (formData.userData.email || formData.businessData.name) {
        setSavingState('saving');
        try {
          localStorage.setItem('businessRegistration', JSON.stringify(formData));
          localStorage.setItem('businessRegistrationStep', step);
          setSavingState('saved');
          setLastSaved(new Date());
          setHasUnsavedChanges(false);
        } catch (error) {
          console.error('Failed to save registration data', error);
          setSavingState('error');
        }
      }
    };

    const timer = setTimeout(saveData, 1000);
    return () => clearTimeout(timer);
  }, [formData, step]);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [formData]);

  const goToStep = (newStep: RegistrationStep) => {
    setStep(newStep);
    // Clear relevant errors when switching steps
    if (newStep === 'personal') setPersonalStepErrors({});
    if (newStep === 'business') setBusinessStepErrors({});
    if (newStep === 'contact') setContactStepErrors({});
    if (newStep === 'location') setLocationStepErrors({});
    if (newStep === 'experience') setExperienceStepErrors({});
  };

  // Handle personal step validation and transition
  const handlePersonalStepNext = async () => {
    setStepIsLoading(true);
    try {
      const errors = validatePersonalStep(formData);
      setPersonalStepErrors(errors);
      
      if (Object.keys(errors).length === 0) {
        // Check if email already exists
        try {
          const emailCheckResult = await checkEmailExists(formData.userData.email);
          if (emailCheckResult.exists) {
            setPersonalStepErrors({ email: 'email_exists' });
            toast.error(t('emailAlreadyRegistered'), {
              description: t('emailExistsDescription'),
              duration: 7000,
            });
            return;
          }
        } catch (error) {
          console.error('Email check failed:', error);
          // Continue to next step even if email check fails
        }
        
        toast.success(t('personalInfoValidated'), {
          duration: 2000,
        });
        goToStep('business');
      } else {
        const errorMessage = Object.values(errors)[0];
        toast.error(errorMessage);
      }
    } finally {
      setStepIsLoading(false);
    }
  };

  // Handle business step validation and transition
  const handleBusinessStepNext = async () => {
    setStepIsLoading(true);
    try {
      const errors = validateBusinessStep(formData);
      setBusinessStepErrors(errors);
      
      if (Object.keys(errors).length === 0) {
        // Show toast notification about experience options being customized based on selections
        if (formData.businessData.hasAnimalActivities || formData.businessData.hasAdventureActivities) {
          toast.info(
            `Your experience options will be customized based on the selected ${
              formData.businessData.hasAnimalActivities && formData.businessData.hasAdventureActivities
                ? 'animal and adventure activities'
                : formData.businessData.hasAnimalActivities
                ? 'animal activities'
                : 'adventure activities'
            }.`,
            { id: 'activity-types-info', duration: 3000 }
          );
        }
        goToStep('contact');
      } else {
        const errorMessage = Object.values(errors)[0];
        toast.error(errorMessage);
      }
    } finally {
      setStepIsLoading(false);
    }
  };

  // Handle contact step validation and transition
  const handleContactStepNext = async () => {
    setStepIsLoading(true);
    try {
      const errors = validateContactStep(formData);
      setContactStepErrors(errors);
      
      if (Object.keys(errors).length === 0) {
        goToStep('location');
      } else {
        const errorMessage = Object.values(errors)[0];
        toast.error(errorMessage);
      }
    } finally {
      setStepIsLoading(false);
    }
  };

  // Handle location step transition - validation happens inside the component
  const handleLocationStepNext = () => {
    goToStep('locationConfirmation');
  };

  // Add new handler for location confirmation
  const handleLocationConfirmationNext = () => {
    goToStep('experience');
  };

  // Update handleExperienceStepNext to go to review step
  const handleExperienceStepNext = () => {
    // Simple validation: ensure at least one experience is selected
    if (!formData.businessData.experiences || formData.businessData.experiences.length === 0) {
      const errorMsg = 'Please select at least one experience category.';
      setExperienceStepErrors({ experiences: errorMsg });
      toast.error('Missing Information', {
        description: errorMsg,
        duration: 3000,
      });
      return; // Stop execution if validation fails
    }

    // Clear any previous errors for this step if validation passes
    setExperienceStepErrors({});
    goToStep('review');
  };

  // Handle review step transition 
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Show initial processing toast
      const processingToast = toast.loading('Checking registration details...', {
        duration: Infinity,
      });

      try {
        // Check if email exists first
        const emailCheckResult = await checkEmailExists(formData.userData.email);

        if (emailCheckResult.exists) {
          toast.dismiss(processingToast);
          toast.error(t('emailAlreadyRegistered'), {
            description: emailCheckResult.message || t('emailExistsDescription'),
            duration: 7000,
            action: {
              label: 'Edit Email',
              onClick: () => {
                setStep('business');
                const emailInput = document.getElementById('email');
                if (emailInput) emailInput.focus();
              }
            }
          });
          setBusinessStepErrors(prev => ({
            ...prev,
            email: 'email_exists'
          }));
          setStep('business');
          return;
        }

        // Update toast message for next step
        toast.loading(t('submittingRegistration'), {
          id: processingToast,
        });

        const slug = generateUniqueSlug(formData.businessData.name);
        console.log('Generated slug:', slug);
    
        // Validate the address coordinates before submitting
        if (!formData.businessData.location.coordinates || 
            !formData.businessData.location.coordinates.coordinates ||
            formData.businessData.location.coordinates.coordinates.length !== 2 ||
            (formData.businessData.location.coordinates.coordinates[0] === 0 &&
             formData.businessData.location.coordinates.coordinates[1] === 0)) {
          throw new Error('VALIDATION_ERROR: Please ensure your business location is properly set');
        }

        // Combine country code and phone number
        const fullPhoneNumber = `${formData.businessData.contact.countryCode}${formData.businessData.contact.phone}`;

        const businessDataWithSlug = {
          ...formData.businessData,
          contact: {
            ...formData.businessData.contact,
            phone: fullPhoneNumber
          },
          slug,
          status: 'pending',
          hasAnimalActivities: formData.businessData.hasAnimalActivities || false,
          hasAdventureActivities: formData.businessData.hasAdventureActivities || false
        };

        const response = await registerBusiness(businessDataWithSlug);
        
        // Dismiss the processing toast
        toast.dismiss(processingToast);

        if (!response || (!response._id && !response.data?._id)) {
          throw new Error('BUSINESS_ERROR: Failed to create business profile');
        }

        const businessId = response._id || response.data?._id;

        // Show success toast for business creation
        toast.success(t('registrationSuccess'), {
          description: 'Now creating your account...',
          duration: 3000,
        });

        const userDataForRegistration = {
          ...formData.userData,
          dateOfBirth: formData.userData.dateOfBirth instanceof Date 
            ? formData.userData.dateOfBirth.toISOString() 
            : new Date(formData.userData.dateOfBirth).toISOString(),
          businessId,
          sendVerificationEmail: true
        };

        try {
          const userResponse = await registerBusinessUser(userDataForRegistration);
          console.log('User registration response:', userResponse);
          
          localStorage.removeItem('businessRegistration');
          localStorage.removeItem('businessRegistrationStep');
          
          setVerificationEmail(formData.userData.email);
          setEmailSent(true);
          
          toast.success(t('registrationSuccess'), {
            description: t('verificationEmailSent'),
            duration: 5000,
          });
        } catch (error: any) {
          console.error('User registration error:', error);
          
          // Dismiss the processing toast if it's still showing
          toast.dismiss(processingToast);
          
          if (error.code === 'EMAIL_EXISTS') {
            setBusinessStepErrors(prev => ({
              ...prev,
              email: 'email_exists'
            }));
            
            setStep('business');
            
            toast.error(t('emailAlreadyRegistered'), {
              description: t('emailExistsDescription'),
              duration: 7000,
              action: {
                label: 'Edit Email',
                onClick: () => {
                  const emailInput = document.getElementById('email');
                  if (emailInput) emailInput.focus();
                }
              }
            });
            return;
          }
          
          const errorMessage = error.details?.message || t('submissionFailed');
          
          toast.error(t('registrationError'), {
            description: errorMessage,
            duration: 7000,
            action: {
              label: 'Try Again',
              onClick: () => handleSubmit()
            }
          });
        }
      } catch (error: any) {
        // Dismiss the processing toast
        toast.dismiss(processingToast);

        if (error.message?.includes('Please provide a valid email')) {
          toast.error('Invalid Email', {
            description: error.message,
            duration: 5000,
          });
          setStep('business');
          return;
        }

        if (error.message?.includes('Network error')) {
          toast.error('Connection Error', {
            description: error.message,
            duration: 5000,
            action: {
              label: 'Retry',
              onClick: () => handleSubmit()
            }
          });
          return;
        }

        console.error('Business creation error:', error);
        
        let title = 'Business Profile Creation Failed';
        let description = error.message || 'There was a problem creating your business profile.';
        let action: { label: string; onClick: () => void } | undefined;
        
        // Handle specific error cases
        if (error.code === 'BUSINESS_EXISTS' || error.response?.data?.code === 'BUSINESS_EXISTS') {
          title = 'Business Already Exists';
          description = 'A business with this name or email is already registered.';
          setStep('business');
        } else if (error.code === 'BUSINESS_CREATION_ERROR' || error.message?.includes('BUSINESS_ERROR')) {
          if (error.details?.status === 400) {
            title = 'Invalid Business Information';
            description = error.details?.message || 'Please check your business information and try again.';
            action = {
              label: 'Review Details',
              onClick: () => setStep('review')
            };
          } else if (error.details?.status === 401) {
            title = 'Session Expired';
            description = 'Your session has expired. Please try again.';
          } else if (error.details?.status === 409) {
            title = 'Business Already Exists';
            description = 'A business with this information is already registered.';
            setStep('business');
          } else if (!error.details?.status) {
            title = 'Connection Error';
            description = 'Please check your internet connection and try again.';
            action = {
              label: 'Retry',
              onClick: () => handleSubmit()
            };
          }
        } else if (error.message?.includes('VALIDATION_ERROR')) {
          title = 'Validation Error';
          description = error.message.replace('VALIDATION_ERROR:', '').trim();
          if (description.toLowerCase().includes('location')) {
            setStep('location');
          }
        }

        toast.error(title, {
          description: description,
          duration: 7000,
          action: action || {
            label: 'Try Again',
            onClick: () => handleSubmit()
          }
        });
      }
    } catch (error: any) {
      console.error('Registration process error:', error);
      
      let title = 'Registration Error';
      let description = 'There was a problem with your registration. Please try again.';
      
      if (error.message?.startsWith('VALIDATION_ERROR:')) {
        title = 'Validation Error';
        description = error.message.replace('VALIDATION_ERROR:', '').trim();
      } else if (error.message?.startsWith('BUSINESS_ERROR:')) {
        title = 'Business Profile Error';
        description = error.message.replace('BUSINESS_ERROR:', '').trim();
      } else if (error.response?.data?.message) {
        description = error.response.data.message;
      }
      
      toast.error(title, {
        description: description,
        duration: 7000,
        action: {
          label: 'Try Again',
          onClick: () => handleSubmit()
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/auth/resend-verification`, {
        email: verificationEmail
      });
      
      if (response.data.success) {
        toast.success('Verification Email Sent!', {
          description: 'Please check your inbox for the new verification link.',
          duration: 5000,
        });
      } else {
        throw new Error(response.data.error || 'Failed to resend verification email');
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      toast.error('Failed to Resend Email', {
        description: error.response?.data?.error || 'Please try again or contact support.',
        duration: 7000,
      });
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-6">
      <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
        <div 
          className="bg-primary h-2 transition-all duration-500 ease-in-out"
          style={{ 
            width: `${((STEP_SEQUENCE.indexOf(step) + 1) / STEP_SEQUENCE.length) * 100}%` 
          }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-2 px-1">
        {STEP_SEQUENCE.map((s, i) => (
          <div 
            key={s} 
            className={`text-center ${step === s ? 'text-primary font-medium' : ''}`}
            style={{ width: `${100 / STEP_SEQUENCE.length}%` }}
          >
            <div className="font-medium">{i + 1}. {STEP_LABELS[s]}</div>
            <div className="text-xs opacity-75">{STEP_DESCRIPTIONS[s]}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const handleCloseModal = () => {
    if (isSubmitting || stepIsLoading) {
      return; // Prevent closing when loading
    }
    
    // Ask for confirmation if there's data
    if (formData.userData.email || formData.businessData.name) {
      if (window.confirm('Your registration progress will be saved. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Add a keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC key to close modal with confirmation if needed
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        handleCloseModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleCloseModal]);

  // Add handleEdit function before the return statement
  const handleEdit = (step: RegistrationStep) => {
    setStep(step);
    toast.info('You can now edit your information', {
      duration: 2000,
    });
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={handleCloseModal}
      aria-labelledby="business-registration-title"
      aria-describedby="business-registration-description"
    >
      <DialogContent 
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto p-4 md:p-6"
        aria-modal="true"
        role="dialog"
      >
        <DialogHeader className="relative">
          <DialogTitle id="business-registration-title">Register Your Wildlife Business</DialogTitle>
          <p id="business-registration-description" className="sr-only">
            Complete the following steps to register your wildlife business with Wildy
          </p>
          {!emailSent && (
            <div className="absolute top-0 right-0">
              <AutoSaveIndicator 
                hasChanges={hasUnsavedChanges}
                savingState={savingState}
                lastSaved={lastSaved}
                variant="compact"
              />
            </div>
          )}
        </DialogHeader>

        {emailSent ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="py-8 text-center space-y-6"
          >
            <div className="mx-auto bg-green-100 w-16 h-16 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-medium text-gray-900">Almost There! 🎉</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                We've sent a verification email to <span className="font-medium">{verificationEmail}</span>.
                Please check your inbox and click the verification link to activate your account.
              </p>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg max-w-md mx-auto">
              <h4 className="font-medium text-blue-800 mb-2">Next Steps:</h4>
              <ol className="text-sm text-blue-700 space-y-2 text-left list-decimal list-inside">
                <li>Check your email and click the verification link</li>
                <li>Once verified, you can log in to your business account</li>
                <li>Complete your business profile setup</li>
                <li>Start managing your wildlife experiences</li>
              </ol>
            </div>
            
            <div className="mt-6 space-y-4">
              <p className="text-sm text-gray-500">
                Didn't receive the email? Check your spam folder or click below to resend.
              </p>
              <Button 
                variant="outline"
                onClick={handleResendVerification}
                className="mx-auto"
              >
                Resend Verification Email
              </Button>
            </div>
            
            <Button 
              onClick={onClose} 
              className="mt-4"
            >
              Close
            </Button>
          </motion.div>
        ) : (
          <>
            {renderStepIndicator()}

            <AnimatePresence mode="wait">
              {step === 'personal' && (
                <PersonalStep
                  key="personal"
                  formData={formData}
                  setFormData={setFormData}
                  onNext={handlePersonalStepNext}
                  isLoading={stepIsLoading}
                  errors={personalStepErrors}
                />
              )}

              {step === 'business' && (
                <BusinessStep
                  key="business"
                  formData={formData}
                  setFormData={setFormData}
                  onNext={handleBusinessStepNext}
                  onBack={() => handleBack('business')}
                  isLoading={stepIsLoading}
                  errors={businessStepErrors}
                />
              )}

              {step === 'contact' && (
                <ContactStep
                  key="contact"
                  formData={formData}
                  setFormData={setFormData}
                  onNext={handleContactStepNext}
                  onBack={() => handleBack('contact')}
                  isLoading={stepIsLoading}
                  errors={contactStepErrors}
                />
              )}

              {step === 'location' && (
                <BusinessLocationStep
                  key="location"
                  formData={formData}
                  setFormData={setFormData}
                  onNext={handleLocationStepNext}
                  onBack={() => handleBack('location')}
                  isLoading={stepIsLoading}
                  errors={locationStepErrors}
                  setErrors={setLocationStepErrors}
                />
              )}

              {step === 'locationConfirmation' && (
                <LocationConfirmationStep
                  key="locationConfirmation"
                  formData={formData}
                  setFormData={setFormData}
                  onNext={handleLocationConfirmationNext}
                  onBack={() => handleBack('locationConfirmation')}
                  isLoading={stepIsLoading}
                  errors={locationStepErrors}
                />
              )}

              {step === 'experience' && (
                <ExperienceStep
                  key="experience"
                  formData={formData}
                  setFormData={setFormData}
                  onBack={() => handleBack('experience')}
                  onNext={handleExperienceStepNext}
                  isSubmitting={isSubmitting}
                  errors={experienceStepErrors}
                />
              )}

              {step === 'review' && (
                <ReviewStep
                  key="review"
                  formData={formData}
                  onBack={() => handleBack('review')}
                  onSubmit={handleSubmit}
                  onEdit={handleEdit}
                />
              )}
            </AnimatePresence>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}